import type {
  GetPlaybookAnalyticsByIdApiResponse,
  MostNegotiatedRequirement,
  PlaybookRecentUse as ServerPlaybookRecentUse,
} from '~/api/atlas/typedefs.autogen';
import type {
  PlaybookDetailAnalytics,
  PlaybookRecentUse,
  PlaybookRecentUseStatus,
  PlaybookRequirementInsight,
} from '~/features/playbooks/types/PlaybookInsights';

// The API spells the status `cancelled`; the UI uses `canceled` throughout.
const STATUS: Record<
  ServerPlaybookRecentUse['status'],
  PlaybookRecentUseStatus
> = {
  done: 'done',
  cancelled: 'canceled',
  in_progress: 'inProgress',
};

const mapRequirement = (
  requirement: MostNegotiatedRequirement,
): PlaybookRequirementInsight => ({
  guidelineId: requirement.guideline_id,
  name: requirement.name,
  reviewsCount: requirement.total_reviews_count ?? 0,
  flagRate: requirement.flag_rate ?? null,
});

const mapRecentUse = (
  recentUse: ServerPlaybookRecentUse,
): PlaybookRecentUse => ({
  ticketId: recentUse.ticket_id,
  name: recentUse.name,
  lastActivityDate: recentUse.last_activity_date,
  redlineReduction: {
    firstRedlinesCount: recentUse.redline_reduction.first_redlines_count,
    firstCriticalRedlinesCount:
      recentUse.redline_reduction.first_critical_redlines_count,
    latestRedlinesCount: recentUse.redline_reduction.latest_redlines_count,
    latestCriticalRedlinesCount:
      recentUse.redline_reduction.latest_critical_redlines_count,
  },
  turnsCount: recentUse.turns_count,
  status: STATUS[recentUse.status],
});

export const mapPlaybookDetailAnalyticsResponse = (
  response: GetPlaybookAnalyticsByIdApiResponse,
): PlaybookDetailAnalytics => {
  const { most_negotiated_requirements, recent_uses } =
    response.data.attributes;

  return {
    mostNegotiatedRequirements: (most_negotiated_requirements ?? []).map(
      mapRequirement,
    ),
    mostNegotiatedRequirementsTotalCount:
      response.meta.most_negotiated_requirements.total,
    recentUses: (recent_uses ?? []).map(mapRecentUse),
  };
};
